/* eslint-disable import/no-cycle */
import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';

import CannotAccess from '../../../CannotAccess';
import {
  featureFlagCacheIsWarm,
  hasFeatureInLocalStorage,
  verifyFeatureAccess,
  verifySentInboxAccess,
} from '../../../../global/featureFlags';

export const SENT_REDESIGN_FLAG = 'sent_redesign';

// `verifyFeatureAccess` can wait on a flags request with no timeout. On a cold cache
// nothing is on screen yet, so after this long fall back to the legacy page rather
// than leave /app/sent blank (same reasoning as the Contacts redesign gate).
export const FLAG_LOOKUP_TIMEOUT_MS = 8000;

const LegacySentRecords = lazy(() => import('../SentRecords'));
const SentRedesignPage = lazy(() => import('./SentRedesignPage'));

// Decides which Sent page to show. A warm localStorage flags cache drives the first
// paint (no round trip) and the server flag converges after mount; a cold cache waits
// for the lookup so the legacy page never mounts, fetches and is then thrown away.
// Any lookup failure lands on legacy: the page that has been in production for years
// beats an unproven one on an error path. Returns { resolved, enabled }.
export const useSentRedesignAccess = (timeoutMs = FLAG_LOOKUP_TIMEOUT_MS) => {
  const [startedWarm] = useState(featureFlagCacheIsWarm);
  const [access, setAccess] = useState(() => (
    startedWarm
      ? { resolved: true, enabled: hasFeatureInLocalStorage(SENT_REDESIGN_FLAG) === true }
      : { resolved: false, enabled: false }
  ));

  useEffect(() => {
    let pending = true;
    let timer = null;

    // Only the first answer counts, so a late lookup cannot swap the page under the user.
    const settle = (enabled) => {
      if (!pending) return;
      pending = false;
      clearTimeout(timer);
      setAccess((current) => (
        current.resolved && current.enabled === enabled ? current : { resolved: true, enabled }
      ));
    };

    if (!startedWarm) timer = setTimeout(() => settle(false), timeoutMs);

    verifyFeatureAccess(SENT_REDESIGN_FLAG)
      .then((hasAccess) => settle(hasAccess === true))
      .catch(() => settle(false));

    return () => {
      pending = false;
      clearTimeout(timer);
    };
  }, [timeoutMs, startedWarm]);

  return access;
};

// The legacy page's own route gate, run before the redesigned page mounts: the same
// verifySentInboxAccess check with the same "Loading..." and CannotAccess states.
// A failed check fails closed to CannotAccess.
export const SentInboxAccessGate = ({ children }) => {
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const allowed = await verifySentInboxAccess();
        if (mounted) setHasAccess(allowed === true);
      } catch {
        if (mounted) setHasAccess(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (hasAccess === null) return <div>Loading...</div>;
  if (!hasAccess) return <CannotAccess />;
  return children;
};

SentInboxAccessGate.propTypes = {
  children: PropTypes.node.isRequired,
};

// /app/sent. Flag off (or unresolvable) renders the existing SentRecords page
// untouched — it keeps running its own access gate.
const SentRecordsSwitch = (props) => {
  const { resolved, enabled } = useSentRedesignAccess();

  if (!resolved) return null;

  if (!enabled) {
    return (
      <Suspense fallback={null}>
        {React.createElement(LegacySentRecords, props)}
      </Suspense>
    );
  }

  return (
    <SentInboxAccessGate>
      <Suspense fallback={null}>
        {React.createElement(SentRedesignPage, props)}
      </Suspense>
    </SentInboxAccessGate>
  );
};

export default SentRecordsSwitch;
